import React, { useState, useEffect, useCallback, useMemo  } from "react";
import {
  Container,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Input,
  Label,
  FormGroup,
  Card,
  CardBody,
  CardTitle,
  Pagination,
  PaginationItem,
  PaginationLink,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Modal,
  ModalBody
} from "reactstrap";
import { getAuthUser } from "../../../helpers/utils";
import { saveDocumentDetails, reviewedDocument } from "../../../helpers/fakebackend_helper";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import * as url from "../../../helpers/url_helper";
import { updateDocumentJson } from "../../../slices/AIDoc/thunk";
import { toast } from "react-toastify";

const hideFields = {
  book_info: [],
  chapters: []
};

/**
 * Recursive editor component:
 * - Renders primitive values as inputs/textareas
 * - Renders objects and arrays recursively
 * - Calls onChange(path, newValue) where path is array of keys/indexes
 */
function RecursiveEditor({ value, path = [], onChange, name, keys, readOnly = false }) {
  const { t } = useTranslation();
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);

  const handlePrimitiveChange = (newVal) => {
    onChange(path, newVal);
  };

  if (isObject) {
    // Pick only allowed keys if `keys` is passed
    // const objectKeys = keys ? keys.filter((k) => Object.prototype.hasOwnProperty.call(value, k)) : Object.keys(value);
    const objectKeys = keys ? Object.keys(value).filter((k) => !keys.includes(k)) : Object.keys(value);

    return (
      <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12, marginBottom: 8 }}>
        {name && <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(name)}</div>}
        {objectKeys.map((key) => (
          <RecursiveEditor
            key={key}
            name={key}
            value={value[key]}
            path={[...path, key]}
            onChange={onChange}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  } else if (isArray) {
    return (
      <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {t(name)} ({t('length')}: {value.length})
        </div>
        {value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <RecursiveEditor
              name={`[${idx}]`}
              value={item}
              path={[...path, idx]}
              onChange={onChange}
              keys={keys} // pass down whitelist for nested objects
              readOnly={readOnly}
            />
            <div>
              <Button
                size="sm"
                color="danger"
                outline
                onClick={() => {
                  const newArr = [...value];
                  newArr.splice(idx, 1);
                  onChange(path, newArr);
                }}
              >
                {t('Remove')}
              </Button>
            </div>
          </div>
        ))}
        <div style={{ marginTop: 6 }}>
          <Button
            size="sm"
            color="primary"
            outline
            onClick={() => {
              const newArr = [...value, typeof value[0] === "object" ? {} : ""];
              onChange(path, newArr);
            }}
          >
            {t('Add item')}
          </Button>
        </div>
      </div>
    );
  } else {
    if (name === "validation_needs_review") {
      return (
        <>
          <FormGroup className="d-flex gap-1" style={{ marginBottom: 8 }}>
            <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
            <Input
              type="checkbox"
              checked={value == true}
              onChange={(e) => handlePrimitiveChange(e.target.checked)}
              readOnly={readOnly}
            />
          </FormGroup>
        </>
      );
    }

    // primitive value
    const asString = value === null ? "" : String(value);
    const useTextarea = asString.length > 60 || asString.includes("\n") || /[اأإآ]/.test(asString);

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
        {useTextarea ? (
          <Input
            type="textarea"
            value={asString}
            rows={5}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        ) : (
          <Input
            type="text"
            value={asString}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        )}
      </FormGroup>
    );
  }
}


/**
 * Helper: update nested value immutably given a path (array of keys/indexes)
 */
function updateAtPath(obj, path, newValue) {
  if (path.length === 0) {
    return newValue;
  }
  const [head, ...rest] = path;
  let clone;
  if (Array.isArray(obj)) clone = [...obj];
  else if (obj && typeof obj === "object") clone = { ...obj };
  else {
    // if path deeper but current is primitive, create object/array depending on next key
    clone = typeof head === "number" ? [] : {};
  }

  clone[head] = updateAtPath(clone[head], rest, newValue);
  return clone;
}

/**
 * Main app
 */
const BookDetails = () => {
  const { t, i18n } = useTranslation();
  const { user } = getAuthUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let { state = {} } = useLocation();
  
  const [isSaving, setIsSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [data, setData] = useState({ books: [{}]});
  const [selectedBookIndex, setSelectedBookIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("book");
  const selectedBook = data.books[selectedBookIndex];

  // Chapter pagination control
  const [chapterIndex, setChapterIndex] = useState(0);
  const [openAccordion, setOpenAccordion] = useState(["1"]);

  const toggleAccordion = (id) => {
    if (openAccordion.includes(id)) {
      setOpenAccordion(openAccordion.filter((x) => x !== id));
    } else {
      setOpenAccordion([...openAccordion, id]); // multiple open allowed
    }
  };

  const onChangeValue = (path, newVal) => {
    // newVal comes as string for primitives; attempt to coerce booleans/numbers/null if appropriate
    const parsedVal = attemptParsePrimitive(newVal);
    const updated = updateAtPath(data, ["books", selectedBookIndex, ...path], parsedVal);
    setData(updated);

    // trigger debounced save
    if (user.RoleID == 118) {
      debouncedSave(updated);
    }
  };

  function attemptParsePrimitive(v) {
    // keep strings by default; but if user typed 'true'/'false'/'null'/'123' we might parse
    if (v === null) return null;
    if (typeof v !== "string") return v;
    const trimmed = v.trim();
    if (trimmed === "") return "";
    if (trimmed === "null") return null;
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    // numeric?
    if (!isNaN(Number(trimmed)) && trimmed.length < 16) {
      return Number(trimmed);
    }
    return v;
  }

  // helper to update top-level fields (document_type etc.)
  const updateBookField = (fieldPath, newValue) => {
    const updated = updateAtPath(data, ["books", selectedBookIndex, ...fieldPath], newValue);
    setData(updated);

    // trigger debounced save
    if (user.RoleID == 118) {
      debouncedSave(updated);
    }
  };

  // export JSON
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "books_edited.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReviewBook = async () => {
    setShowConfirm(false);
    setIsSaving(true);
    try {
      const response = await handleSaveJson(data, true);
      await reviewedDocument({ DocID: state.DocID, IsSaved: true });
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveJson = async (data, pushToPinecone = false) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      data.DocID = state.DocID;
      data.JsonURL = state.JsonURL;
      data.DocumentTypeID = state.DocumentTypeID;
      data.Status = [1, 4, 98].includes(user.RoleID) ? 3 : 4;
      data.pushToPinecone = pushToPinecone;

      const formData = new FormData();
      formData.append("payload", data);

      const response = await fetch(url.SAVE_DOC_DETAILS, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        // headers: { 'Content-Type': "text/plain" },
        body: JSON.stringify(data), 
      });

      if (!response.body) throw new Error("No response body for stream");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      // Process streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // keep last incomplete chunk

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          let eventName = "";
          let eventData = "";

          for (const line of eventBlock.split("\n")) {
            if (line.startsWith("event:"))
              eventName = line.slice(6).trim();
            else if (line.startsWith("data:"))
              eventData += line.slice(5).trim();
          }

          if (!eventName || !eventData) continue;

          try {
            const parsedData = JSON.parse(eventData);

            if (eventName === "file_saved") {
              // ✅ Update state safely
              if (state.JsonURL != parsedData.url) {
                state.JsonURL = parsedData.url;
                state.IsProcessed = true;
                dispatch(updateDocumentJson(state))
                navigate('/ai/book-details', { 
                  state:  {
                    ...state
                  },
                  replace: true
                })
              } else {
                toast.success(t("DocumentDetailsSavedMessage"));
              }
              return parsedData;
            }
          } catch (err) {
            console.warn("Invalid SSE event data:", eventData, err);
          }
        }
      }
    } catch (error) {
      console.error("Save stream error:", error);
    } finally {
      setIsSaving(false); // ✅ only after stream fully ends
    }
  }

  const handleDeleteBook = () => {
    // if (!window.confirm("Are you sure you want to delete this document?")) return;

    const docs = [...data.books];
    docs.splice(selectedBookIndex, 1);
    setShow(false);
    if (docs.length === 0) {
      setData({ books: [{}] }); // fallback to empty book
      setSelectedBookIndex(0);
    } else {
      setData({ ...data, books: docs });
      setSelectedBookIndex(Math.max(0, selectedBookIndex - 1));
    }

    if (user.RoleID == 118) {
      debouncedSave({ ...data, books: docs });
    }
  };

  const debouncedSave = useCallback(
    debounce(handleSaveJson, 3000), // shorter debounce feels snappier
    [state.JsonURL] // correct deps
  );

  // Chapter pagination helpers
  const chapters = selectedBook?.chapters || [];
  const chapterCount = chapters.length;
  const currentArticle = chapters[chapterIndex] || null;


  const gotoChapter = (idx) => {
    const newIdx = Math.max(0, Math.min(chapterCount - 1, idx));
    setChapterIndex(newIdx);
  };

  const chapterPagination = useMemo(() => {
    const total = chapterCount;       // total pages
    const current = chapterIndex;     // current page index (0-based)
    const maxVisible = 7;             // max pages to display in window
    const pages = [];

    if (total <= 1) return [];
    // if (total <= 1) return null;

    // Always include first page
    pages.push(0);

    // Case 1: if total pages ≤ maxVisible → show all
    if (total <= maxVisible) {
      for (let i = 1; i < total; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Define sliding window
    let start = Math.max(1, current - 2);
    let end = Math.min(total - 2, current + 2);

    // Ensure window always has (maxVisible - 2) slots (excluding first & last)
    const windowSize = maxVisible - 2;
    if (end - start + 1 < windowSize) {
      if (current <= Math.floor(total / 2)) {
        end = Math.min(total - 2, start + windowSize - 2);
      } else {
        start = Math.max(1, end - windowSize + 1);
      }
    }

    // Left ellipsis
    if (start > 1) {
      pages.push("ellipsis-left");
    }

    // Middle pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Right ellipsis
    if (end < total - 2) {
      pages.push("ellipsis-right");
    }

    // Always include last page
    pages.push(total - 1);

    return pages;
  }, [chapterCount, chapterIndex]);


  useEffect(() => {
    const fetchJson = async () => {
      try {
        if (!state.JsonURL) return;
        // const response = await fetch("https://storage.googleapis.com/rag-legal-docs-dev/final_output/1757680360186.json");
        const response = await fetch(state.JsonURL);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        console.error("Error fetching JSON:", err);
      }
    };

    fetchJson();
  }, [state.JsonURL]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <CardTitle tag="h4">{t('Book Editor')}</CardTitle>
                <Row className="align-items-center">
                  <Col md="6">
                    <div className="d-flex gap-2 justify-content-start">
                      <Button color="secondary" onClick={() => navigate(-1)}>
                        <i className={`${i18n.language === 'en' ? "ri-arrow-left-line" : "ri-arrow-right-line"}`} />
                      </Button>
                      <UncontrolledDropdown>
                        <DropdownToggle caret>
                          {t('Book')}:
                          {selectedBook?.book_uid
                            ? `- ${selectedBook?.book_uid}`
                            : ""}
                        </DropdownToggle>
                        <DropdownMenu>
                          {Object.keys(data).length > 0 && data.books.map((book, idx) => (
                            <DropdownItem
                              key={idx}
                              onClick={() => {
                                setSelectedBookIndex(idx);
                                setChapterIndex(0);
                              }}
                            >
                              #{idx} — {book?.book_uid || book?.book_info?.isbn}
                            </DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  </Col>
                  <Col md="6" className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      { ([1, 4, 98].includes(user.RoleID)) && <>
                        <Button onClick={exportJSON}>
                          {t('Export JSON')}
                        </Button>
                        <Button color="success" onClick={() => handleSaveJson(data)} disabled={isSaving}>
                          {t('Save')}
                        </Button> 
                      </>
                      }
                      {  user.RoleID == 118 &&
                        <Button color="success" onClick={() => setShowConfirm(true)} disabled={isSaving}>
                          {t('Reviewed')}
                        </Button> 
                      }
                      {/* 🔹 New Delete Button */}
                      { data.books?.length > 1 &&
                        <Button color="danger" onClick={() => setShow(true)}>
                          {t('Delete Book')}
                        </Button>
                      }
                    </div>
                  </Col>
                </Row>

                <Nav tabs style={{ marginTop: 16 }}>
                  <NavItem>
                    <NavLink
                      className={activeTab === "book" ? "active" : ""}
                      onClick={() => setActiveTab("book")}
                      style={{ cursor: "pointer" }}
                    >
                      {t('Book')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "chapter" ? "active" : ""}
                      onClick={() => setActiveTab("chapter")}
                      style={{ cursor: "pointer" }}
                    >
                      {t('Chapter')}
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} style={{ marginTop: 12 }}>
                  <TabPane tabId="book">
                    <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto", // vertical scrollbar for PDF
                        }}>
                        <Accordion open={openAccordion} toggle={toggleAccordion}>

                          {/* Book Info */}
                          <AccordionItem>
                            <AccordionHeader targetId="1">{t('Book Info')}</AccordionHeader>
                            <AccordionBody accordionId="1">
                              <RecursiveEditor
                                name="book_info"
                                value={selectedBook.book_info}
                                path={["book_info"]}
                                onChange={onChangeValue} 
                              />
                            </AccordionBody>
                          </AccordionItem>

                          {/* Annexes */}
                          <AccordionItem>
                            <AccordionHeader targetId="2">{t('annexes')}</AccordionHeader>
                            <AccordionBody accordionId="2">
                              <RecursiveEditor
                                name="annexes"
                                value={selectedBook.annexes}
                                path={["annexes"]}
                                onChange={onChangeValue} 
                              />
                            </AccordionBody>
                          </AccordionItem>
                        </Accordion>
                      </Col>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}>
                        <iframe
                          src={state.FileURL || ''}
                          width="100%"
                          height="95%"
                          title="PDF Viewer"
                          style={{ minHeight: "570px" }}
                          ></iframe>
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId="chapter">
                    <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                      <Col md={6}
                        style={{
                          height: "100%",
                          overflowY: "auto", // vertical scrollbar for PDF
                        }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{t('Total Articles')}</strong>{" "}
                            <small className="text-muted">({chapterCount})</small>
                          </div>
                          <div>
                            <Button
                              size="sm"
                              color="secondary"
                              outline
                              onClick={() => {
                                // add new blank chapter
                                const newChapters = [...(selectedBook.chapters || []), { 
                                  chunk_type: "", 
                                  chapter_number: "",
                                  chapter_title_ar: "",
                                  content: ""
                                }];
                                updateBookField(["chapters"], newChapters);
                                setChapterIndex(chapterCount);
                              }}
                            >
                              {t('Add Chapter')}
                            </Button>
                          </div>
                        </div>

                        {chapterCount === 0 ? (
                          <div style={{ marginTop: 12 }}>{t('NoArticlesAvailable')}</div>
                        ) : (
                          <>
                            <Card style={{ marginTop: 12 }}>
                              <CardBody>
                                <h6>{t('Chapter')} {currentArticle?.chapter_number}</h6>

                                {/* Dropdown to move chapter */}
                                <UncontrolledDropdown className="mb-3">
                                  <DropdownToggle caret>
                                    {t('Move to Book')}:
                                    {selectedBook?.book_uid
                                      ? `- ${selectedBook?.book_uid}`
                                      : ""}
                                  </DropdownToggle>
                                  <DropdownMenu>
                                    {data.books.map((book, idx) => (
                                      <DropdownItem
                                        key={idx}
                                        active={idx === selectedBookIndex}
                                        onClick={() => {
                                          if (idx === selectedBookIndex) return; // already in same book
                                          const articleToMove = currentArticle;

                                          // remove from current book
                                          const fromDocs = [...data.books];
                                          fromDocs[selectedBookIndex] = {
                                            ...fromDocs[selectedBookIndex],
                                            chapters: fromDocs[selectedBookIndex].chapters.filter(
                                              (_, i) => i !== chapterIndex
                                            ),
                                          };

                                          // append into target book
                                          fromDocs[idx] = {
                                            ...fromDocs[idx],
                                            chapters: [...(fromDocs[idx].chapters || []), articleToMove],
                                          };

                                          // update state
                                          setData({ ...data, books: fromDocs });
                                          if (user.RoleID == 118) {
                                            debouncedSave({ ...data, books: fromDocs })
                                          }

                                          // reset chapter index
                                          setChapterIndex(Math.min(chapterIndex, fromDocs[selectedBookIndex].chapters.length - 1));
                                        }}
                                      >
                                        #{idx} — {book?.book_uid|| book?.book_info?.isbn}
                                      </DropdownItem>
                                    ))}
                                  </DropdownMenu>
                                </UncontrolledDropdown>

                                <RecursiveEditor
                                  // name={`article_${chapterIndex}`}
                                  value={currentArticle}
                                  path={["chapters", chapterIndex]}
                                  onChange={onChangeValue} 
                                  keys={hideFields.chapters}
                                />
                              </CardBody>
                            </Card>

                            <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 12 }}>
                              <Pagination>
                                <PaginationItem disabled={chapterIndex === 0}>
                                  <PaginationLink previous onClick={() => gotoChapter(chapterIndex - 1)} />
                                </PaginationItem>

                                {chapterPagination.map((p, idx) =>
                                  p === "ellipsis-left" || p === "ellipsis-right" ? (
                                    <PaginationItem key={idx} disabled>
                                      <PaginationLink>...</PaginationLink>
                                    </PaginationItem>
                                  ) : (
                                    <PaginationItem active={p === chapterIndex} key={idx}>
                                      <PaginationLink onClick={() => gotoChapter(p)}>
                                        {p + 1}
                                      </PaginationLink>
                                    </PaginationItem>
                                  )
                                )}


                                <PaginationItem disabled={chapterIndex === chapterCount - 1}>
                                  <PaginationLink next onClick={() => gotoChapter(chapterIndex + 1)} />
                                </PaginationItem>
                              </Pagination>
                          

                              <div>
                                <Button
                                  size="sm"
                                  color="danger"
                                  outline
                                  onClick={() => {
                                    // remove current chapter
                                    const arr = [...selectedBook.chapters];
                                    arr.splice(chapterIndex, 1);
                                    updateBookField(["chapters"], arr);
                                    gotoChapter(Math.max(0, chapterIndex - 1));
                                  }}
                                  disabled={chapterCount === 0}
                                >
                                  {t('Delete current chapter')}
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </Col>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}>
                        <iframe
                          src={state.FileURL || ''}
                          width="100%"
                          height="95%"
                          title="PDF Viewer"
                          style={{ minHeight: "600px" }}
                          ></iframe>
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row> 
      </Container>

      <Modal isOpen={show} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('areYouSure')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Are you sure you want to delete this document?')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShow(false)}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger d-flex align-items-center"
              onClick={handleDeleteBook}
            >
              {t('yesDelete')}
            </button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={showConfirm} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('areYouSure')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Are you sure you want to review this document?')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShowConfirm(false)}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger d-flex align-items-center"
              disabled={isSaving}
              onClick={handleReviewBook}
            >
              {t('Yes, Reviewed it')}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default BookDetails;